import { HeaderService } from "@app/components/header/header.service";
import { GuestPaymentStore } from "@app/services/stores/GuestPaymentStore";
import { PropertyApi } from "@app/services/apiServices/PropertyApi";
import { Property } from "@app/models/Property";
import { Amenity } from "@app/models/Amenity";
import { PaymentRequestDetail } from "@app/models/PaymentRequestDetail";
import { PaymentRequest } from "@app/models/PaymentRequest";
import { PaymentEntityType } from "@app/models/PaymentEntityType";
import { Payment } from "@app/models/Payment";
import { moneyNumber } from "@app/util/money-number";
import { AmenityNameCatalog } from "@app/models/AmenityNameCatalog";
import { AmenityService } from "@app/services/Amenity.service";
import BadWordsFilter = require("bad-words");
import { Component, EventEmitter, Inject, Input, OnInit, Output } from "@angular/core";
import { AppDataService } from "@app/services/appData.service";
import { MatDialog } from "@angular/material/dialog";
import { WindowToken } from "@app/util/window.token";
import { AmenityNoteDialog } from "@app/components/selectAmenities/amenity-note.dialog";
import { take } from "rxjs/operators";
import { Router } from "@angular/router";

class SelectAmenities implements OnInit {

    @Input() isGuest: boolean;
    @Input() property: Property;
    @Input() cartItems: PaymentRequestDetail[];
    @Input() itemsTotal: number;
    @Input() availableAmenities: Amenity[];
    @Input() availableCategories: AmenityNameCatalog[];
    @Input() filteredAmenities: Amenity[];
    @Input() selectedCategory: AmenityNameCatalog;
    @Input() allowPayByCheck: boolean;
    @Input() allowPayByCard: boolean;
    @Input() componentType: string;

    @Output() onCartItemUpdate = new EventEmitter<void>();
    @Output() onCartItemAdd = new EventEmitter<void>();

    selectedAmenity: Amenity;
    badWordsFilter: BadWordsFilter = new BadWordsFilter();
    maxPrincipalAmount: number;
    amenityAmtErrorMsg: string;

    getWebUrl: () => string;

    constructor(
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(PropertyApi) private propertyApi: PropertyApi,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(WindowToken) private $window: Window,
        @Inject(HeaderService) private headerService: HeaderService,
        @Inject(GuestPaymentStore) private guestPaymentStore: GuestPaymentStore,
        @Inject(AmenityService) private amenityService: AmenityService,
        @Inject(Router) private router: Router) {}

    ngOnInit() {
        this.getWebUrl = () => this.appDataService.appConfig.get_APP_ENV().HOAPASS_WEB_URL;
        this.headerService.setDisplayRightContent(typeof this.isGuest === 'undefined');
        this.badWordsFilter.addWords(... this.appDataService.appSettings.badwords);
        this.maxPrincipalAmount = this.appDataService.appSettings.paymentLimits.maxPrincipalAmount;
    }

    changeSelectedCategory() {
        if (this.selectedCategory.id === null) {
            this.filteredAmenities = this.availableAmenities;
        } else {
            this.filteredAmenities =
                this.availableAmenities.filter(amenity => amenity.amenityNameCatalog.id === this.selectedCategory.id);
        }
    }

    addAmenityToCart() {
        if (this.selectedAmenity !== undefined) {
            let foundCartItem = this.cartItems.find(cartItem => cartItem.entityId === this.selectedAmenity.id);
            if (foundCartItem != null)
                this.incrementQty(foundCartItem);
            else {
                let newCartItem: PaymentRequestDetail = {
                    entityId: this.selectedAmenity.id,
                    type: PaymentEntityType.AMENITY,
                    qty: 1,
                    pmcAmount: this.selectedAmenity.pmcAmount,
                    hoaAmount: this.selectedAmenity.hoaAmount,
                    amount: this.selectedAmenity.totalAmount,
                    description: this.selectedAmenity.description,
                    displayNotes: this.selectedAmenity.displayNotes,
                    inventoryCount: this.selectedAmenity.count
                }

                this.cartItems.push(newCartItem);
                this.updateTotal();
                this.amenityService.validateCartItem(newCartItem);
            }

            this.onCartItemAdd.emit();
        }
    }

    decrementQty(cartItem: PaymentRequestDetail) {
        if (cartItem.qty > 1) {
            cartItem.qty--;
            this.updateTotal();
            this.amenityService.validateCartItem(cartItem);
            this.onCartItemUpdate.emit()
        }
    }

    incrementQty(cartItem: PaymentRequestDetail) {
        if (cartItem.qty < 9999) {
            cartItem.qty++;
            this.updateTotal();
            this.amenityService.validateCartItem(cartItem);
            this.onCartItemUpdate.emit()
        }
    }

    deleteCartItem(cartItem: PaymentRequestDetail) {
        this.cartItems.splice(this.cartItems.indexOf(cartItem),1);
        this.updateTotal();
        this.onCartItemUpdate.emit()
    }

    updateTotal() {
        let tempTotal = 0;
        if(this.cartItems && this.cartItems.length > 0) {
            this.cartItems.forEach(cartItem => {
                tempTotal += cartItem.qty * cartItem.amount;
            })
        }
        this.itemsTotal = moneyNumber(tempTotal);
        if (tempTotal > this.maxPrincipalAmount) {
            let maxPrincipalAmt = new Intl.NumberFormat( 'en-US', { currency : 'USD' }).format(this.maxPrincipalAmount);
            this.amenityAmtErrorMsg = 'Order Total cannot exceed $' + maxPrincipalAmt;
        } else {
            this.amenityAmtErrorMsg = undefined;
        }
    }

    submit(type: 'check' | 'card') {
        let paymentRequest: PaymentRequest = {
            acceptedTerms: true,
            acceptedTermsDate: '',
            details: this.cartItems,
            payment: {
                principalAmount: this.itemsTotal,
                feeAmount: 0,
                totalPaymentAmount: this.itemsTotal
            }
        }
        this.guestPaymentStore.setPaymentRequest(paymentRequest);
        this.guestPaymentStore.setPayment(this.getPayment());

        if (type === "check") {
            this.router.navigateByUrl('/amenities/guestpaymentecheck');
        } else {
            this.router.navigateByUrl('/amenities/guestpaymentcard');
        }
    }

    getPayment(): Payment {
        return {
            feeAmount: null,
            totalPaymentAmount: null,
            principalAmount: moneyNumber(this.itemsTotal),
        }
    }

    onClickBack() {
        this.router.navigateByUrl('/amenities/guestpayment');
    }

    addEditNote(cartItem: PaymentRequestDetail) {
        let dialogRef = this.dialog.open(AmenityNoteDialog, {
            data: {
                cartItem: cartItem,
                property: this.property,
                payerNotes: cartItem.payerNotes,
                changeFn: this.isProfane,
                badWordsFilter: this.badWordsFilter
            }
        })
        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
            if (value.save && value.text) {
                cartItem.payerNotes = value.text
            } else if (value.save && !value.text) {
                cartItem.payerNotes = null;
            }
        }, () => {})
    }

    isProfane(dialogData) {
        if (dialogData.badWordsFilter.isProfane(dialogData.payerNotes)) {
            dialogData.payerNotesError = 'Note must contain appropriate verbiage';
        } else {
            dialogData.payerNotesError = null;
        }
    }
}

@Component({
    selector: 'app-select-guest-amenities',
    template: require('./selectGuestAmenities.directive.html')
})
export class SelectGuestAmenitiesComponent extends SelectAmenities{}

@Component({
    selector: 'app-select-amenities',
    template: require('./selectAmenities.directive.html')
})
export class SelectAmenitiesComponent extends SelectAmenities{}


@Component({
    selector: 'app-select-amenities-cart',
    template: require('./selectAmenitiesCart.directive.html')
})
export class SelectAmenitiesCartComponent extends SelectAmenities{}
